import { FC, useCallback, useMemo } from 'react'
import {
  Text,
  GreyGrey60,
  LineItemGroup,
  Primary13Regular,
  Flex
} from '@sequencehq/core-components'
import { Currency } from '@sequencehq/api'
import { AggregatedLineItemGroup, LineItem } from 'CreditNotes/types'
import { match } from 'ts-pattern'
import { parseFloatOrZero } from 'lib/parsers'
import { MAX_INPUT_TOTAL, MAX_QUANTITY } from 'CreditNotes/constants'
import { CreditNoteAddLineItem } from './CreditNoteAddLineItem'
import {
  EditCreditNoteLineItemDrawer,
  SubmitValues
} from './drawers/EditCreditNoteLineItemDrawer'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { formatServicePeriodWithFallback } from '@sequencehq/invoice-content'
import { RevRecLineItemValidationHints } from './RevRecLineItemValidationHints'

type Props = {
  creditNoteId: string
  lineItemGroups: AggregatedLineItemGroup[]
  updateLineItems: (lineItemsToUpdate: LineItem[]) => void
  currency: Currency
  editable?: boolean
  editingLineItemId: string | null
  onEditLineItem: (id: string | null) => void
  validationTriggered?: boolean
  billingPeriodStart?: string
  billingPeriodEnd?: string
}

export const CreditNoteLineItems: FC<Props> = ({
  creditNoteId,
  lineItemGroups,
  updateLineItems,
  currency,
  editable = false,
  editingLineItemId,
  onEditLineItem,
  validationTriggered = false,
  billingPeriodStart,
  billingPeriodEnd
}) => {
  const { showServicePeriodsAlways } = useFeatureFlags()

  const editDrawLineItem = useMemo(() => {
    return lineItemGroups
      .flatMap(group => group.lineItems)
      .find(item => item.id === editingLineItemId)
  }, [lineItemGroups, editingLineItemId])

  const handleAdvancedEditSubmit = (formValues: SubmitValues) => {
    if (!editDrawLineItem) {
      return Promise.resolve(true)
    }
    updateLineItems([
      {
        ...editDrawLineItem,
        ...formValues,
        productId: formValues.productId || undefined,
        taxCategoryId: formValues.taxCategoryId || undefined
      }
    ])
    return Promise.resolve(true)
  }

  return (
    <Flex gap={4} direction="column" data-testid="creditNote.lineItems">
      {editDrawLineItem && (
        <EditCreditNoteLineItemDrawer
          isOpen
          onClose={() => onEditLineItem(null)}
          onSubmit={handleAdvancedEditSubmit}
          creditNoteCurrency={currency}
          creditNoteLineItem={editDrawLineItem}
        />
      )}

      {lineItemGroups.map(group => {
        return (
          <LineItemGroup
            key={group.id}
            variant="creditNotes"
            currency={currency}
            onChangeSelected={
              !group.lineItems.length || !editable
                ? undefined
                : selected => {
                    updateLineItems(
                      group.lineItems.map(lineItem => {
                        return { ...lineItem, selected }
                      })
                    )
                  }
            }
            selected={match(
              group.lineItems.filter(item => item.selected).length
            )
              .with(0, () => false)
              .with(group.lineItems.length, () => true)
              .otherwise(() => 'indeterminate')}
          >
            {group.lineItems.map(lineItem => {
              const formattedServicePeriod = showServicePeriodsAlways
                ? formatServicePeriodWithFallback({
                    start: lineItem.servicePeriod?.start,
                    end: lineItem.servicePeriod?.endInclusive,
                    fallbackStart: billingPeriodStart,
                    fallbackEnd: billingPeriodEnd
                  })
                : undefined

              return (
                <CreditNoteLineItem
                  key={lineItem.id}
                  lineItem={lineItem}
                  editable={editable}
                  updateLineItems={updateLineItems}
                  onTriggerAdvancedEdit={() => onEditLineItem(lineItem.id)}
                  validationTriggered={validationTriggered}
                  formattedServicePeriod={formattedServicePeriod}
                />
              )
            })}
            {editable && (
              <CreditNoteAddLineItem
                creditNoteId={creditNoteId}
                groupId={group.id}
                currency={currency}
              />
            )}
            <LineItemGroup.Subtotal value={group.netTotal} />
          </LineItemGroup>
        )
      })}
    </Flex>
  )
}

const CreditNoteLineItem: FC<{
  lineItem: LineItem
  editable: boolean
  updateLineItems: (lineItemsToUpdate: LineItem[]) => void
  onTriggerAdvancedEdit: () => void
  validationTriggered: boolean
  formattedServicePeriod?: string
}> = ({
  lineItem,
  editable,
  updateLineItems,
  onTriggerAdvancedEdit,
  validationTriggered,
  formattedServicePeriod
}) => {
  const LineItemComponent = editable
    ? LineItemGroup.EditableLineItem
    : LineItemGroup.LineItem

  const showHints = editable && validationTriggered

  const handleChangeSelected = useCallback(
    (selected: boolean) => {
      updateLineItems([{ ...lineItem, selected }])
    },
    [updateLineItems, lineItem]
  )

  const handleChangeTotal = useCallback(
    (netTotal: string) => {
      const parsedTotal = parseFloatOrZero(netTotal)
      const limitedTotal =
        parsedTotal > MAX_INPUT_TOTAL ? lineItem.netTotal : netTotal

      updateLineItems([
        {
          ...lineItem,
          netTotal: limitedTotal,
          quantity: '1',
          rate: parsedTotal.toString()
        }
      ])
    },
    [updateLineItems, lineItem]
  )

  const handleChangeQuantity = useCallback(
    (quantity: string) => {
      const parsedQuantity = parseFloatOrZero(quantity)
      const limitedQuantity =
        parsedQuantity > MAX_QUANTITY ? lineItem.quantity : quantity

      updateLineItems([{ ...lineItem, quantity: limitedQuantity }])
    },
    [updateLineItems, lineItem]
  )

  const handleDelete = useCallback(() => {
    updateLineItems([{ ...lineItem, selected: true, deleted: true }])
  }, [updateLineItems, lineItem])

  return (
    <LineItemComponent
      selected={lineItem.selected}
      key={lineItem.id}
      title={lineItem.title}
      quantity={lineItem.quantity}
      rate={lineItem.rate}
      rateDisplay={lineItem.rateDisplay}
      taxRate={lineItem.taxRate}
      netTotal={lineItem.netTotal}
      inputsDisabled={!lineItem.selected}
      onChangeQuantity={handleChangeQuantity}
      onChangeSelected={handleChangeSelected}
      onChangeTotal={handleChangeTotal}
      onTriggerAdvancedEdit={onTriggerAdvancedEdit}
      onDelete={handleDelete}
      testIdPrefix={`${lineItem.id}.`}
      subtitleContent={
        <>
          {formattedServicePeriod && (
            <Text {...Primary13Regular} color={GreyGrey60}>
              {formattedServicePeriod}
            </Text>
          )}
          <RevRecLineItemValidationHints
            showHints={showHints}
            servicePeriod={lineItem.servicePeriod}
            revenueRecognitionMethod={lineItem.revenueRecognitionMethod}
            onTriggerAdvancedEdit={onTriggerAdvancedEdit}
          />
        </>
      }
    />
  )
}
